import useInventoryItems, {
  InventoryItem,
  InventoryItemType,
} from "../../../utils/hooks/inventory/useInventoryItems";
import { useCustomAlert } from "../../../utils/hooks/alerts";
import LoadingOverlay from "../../LoadingOverlay";
import { ModalContainer } from "../Posts/EditPostStyles";
import { Title } from "./Bins/BinsStyles";
import { Formik } from "formik";
import { FormContainer } from "../../../constants/Styles";
import FormikFormLine from "../../formik-form-components/FormikFormLine";
import FormikSelect from "../../formik-form-components/FormikSelect";
import FormikNumberInput from "../../formik-form-components/FormikNumberInput";
import ContainerFormButtons from "../../component-search/PartDrawer/Inventory/PhysicalInventory/ContainerFormButtons";
import React from "react";
import { StencilType } from "../../../reducers/uploadedBoard";
import * as Yup from "yup";
import { FLASHPCB_CONTAINER_BARCODE_REGEX } from "../../component-search/PartDrawer/Inventory/PhysicalInventory/constants";
import { FLASHPCB_BIN_BARCODE_REGEX } from "./InventoryBinForm";
import useBoardBin from "../../../utils/hooks/commonly-stored/uploaded-board/boardBin";
import BinBarcodeField from "../../component-search/PartDrawer/Inventory/PhysicalInventory/BinBarcodeField";
import { InventoryItemFormContainer } from "./InventoryUpdates/InventoryUpdatesStyles";
import Button from "../../common/Button";
import { usePrintBarcode } from "../../../utils/hooks/inventory/printBarcode";

const STENCIL_TYPE_OPTIONS = {
  top: { value: StencilType.TOP, label: "Top" },
  bottom: { value: StencilType.BOTTOM, label: "Bottom" },
  dual_image: { value: StencilType.DUAL_IMAGE, label: "Dual image" },
};

export interface InventoryItemUpdateValues {
  item_type: InventoryItemType;
  flashpcb_container_barcode: string;
  flashpcbBinBarcode: string;
  stencil_type: StencilType | null;
  boards_per_panel: number | null;
  last_counted_amount: number;
  nickname: string;
  notes: string;
}

const inventoryItemSchema = Yup.object().shape({
  item_type: Yup.string().oneOf(Object.values(InventoryItemType)).required(),
  flashpcb_container_barcode: Yup.string()
    .required("FlashPCB container barcode is required")
    .matches(new RegExp(FLASHPCB_CONTAINER_BARCODE_REGEX)),
  flashpcbBinBarcode: Yup.string()
    .required("FlashPCB bin barcode is required")
    .matches(new RegExp(FLASHPCB_BIN_BARCODE_REGEX)),
  stencil_type: Yup.mixed()
    .oneOf(Object.values(STENCIL_TYPE_OPTIONS).map((option) => option.value))
    .nullable(),
  nickname: Yup.string().nullable(),
  notes: Yup.string().nullable(),
  last_counted_amount: Yup.number().min(0),
});

export default function AddOrUpdateInventoryItem({
  itemType,
  itemToUpdate,
  onSuccess,
  onCancel,
  boardId,
}: {
  itemType: InventoryItemType;
  itemToUpdate: InventoryItem | null;
  onCancel: () => void;
  onSuccess?: () => void;
  boardId?: string;
}) {
  const { inventoryBin: boardBin } = useBoardBin(boardId || null);
  const { createBarcode } = usePrintBarcode();
  const customAlert = useCustomAlert();
  const onErrorUpdatingItem = (detail: string) => {
    customAlert(detail);
  };
  const { updateInventoryItem, loading } = useInventoryItems(
    onSuccess,
    onErrorUpdatingItem,
  );

  const onSubmit = (values: InventoryItemUpdateValues) => {
    let stencil_details = null;
    let panel_details = null;
    if (values.stencil_type) {
      stencil_details = {
        stencil_type: values.stencil_type,
      };
    }

    if (values.boards_per_panel) {
      panel_details = {
        boards_per_panel: values.boards_per_panel,
      };
    }

    updateInventoryItem(
      {
        item_type: values.item_type,
        flashpcb_container_barcode: values.flashpcb_container_barcode,
        flashpcb_bin_barcode: values.flashpcbBinBarcode,
        stencil_item_details: stencil_details,
        panel_item_details: panel_details,
        nickname: values.nickname,
        last_counted_amount: values.last_counted_amount,
        notes: values.notes,
        board_ids: boardId ? [boardId] : null,
      },
      itemToUpdate === null,
    );
  };
  return (
    <InventoryItemFormContainer>
      <LoadingOverlay loading={loading} text={"Updating item..."} />
      <ModalContainer>
        <Title>{`Update ${itemType}`}</Title>
        <Formik
          initialValues={{
            item_type: itemType,
            flashpcb_container_barcode: itemToUpdate
              ? itemToUpdate.flashpcb_container_barcode
              : "",
            flashpcbBinBarcode: itemToUpdate
              ? itemToUpdate.flashpcb_bin_barcode
              : boardBin
                ? boardBin.flashpcb_bin_barcode
                : "",
            stencil_type:
              itemToUpdate && itemToUpdate.stencil_item_details
                ? itemToUpdate.stencil_item_details.stencil_type
                : null,
            boards_per_panel:
              itemToUpdate && itemToUpdate.panel_item_details
                ? itemToUpdate.panel_item_details.boards_per_panel
                : null,
            nickname: itemToUpdate ? itemToUpdate.nickname : "",
            notes: itemToUpdate ? itemToUpdate.notes : "",
            last_counted_amount: itemToUpdate
              ? itemToUpdate.last_counted_amount
              : 1,
          }}
          onSubmit={onSubmit}
          validationSchema={inventoryItemSchema}
        >
          {({ values, handleSubmit, isValid, dirty }) => {
            return (
              <FormContainer onSubmit={handleSubmit}>
                <FormikFormLine
                  name={"flashpcb_container_barcode"}
                  label={"FlashPCB Container Barcode"}
                  disabled={false}
                  autoFocus={true}
                />
                <Button
                  label="Print additional label"
                  onClick={() =>
                    createBarcode(values.flashpcb_container_barcode)
                  }
                  disabled={values.flashpcb_container_barcode.length === 0}
                />
                <BinBarcodeField binBarcode={values.flashpcbBinBarcode} />
                {itemType === InventoryItemType.STENCIL && (
                  <FormikSelect
                    name={"stencil_type"}
                    label={"Stencil type"}
                    disabled={false}
                    options={Object.values(STENCIL_TYPE_OPTIONS)}
                  />
                )}
                {itemType === InventoryItemType.PANELS && (
                  <FormikNumberInput
                    name={"boards_per_panel"}
                    label={"Boards per panel"}
                    disabled={false}
                  />
                )}
                <FormikFormLine
                  name={"nickname"}
                  label={"Nickname"}
                  disabled={false}
                  isRequired={false}
                />
                <FormikFormLine
                  name={"notes"}
                  label={"Notes"}
                  isTextArea={true}
                  disabled={false}
                  isRequired={false}
                />
                <FormikNumberInput
                  name={"last_counted_amount"}
                  label={"Quantity"}
                  disabled={false}
                />
                <ContainerFormButtons
                  onCancel={onCancel}
                  loading={false}
                  isDisabled={!(isValid && dirty)}
                  onSubmit={handleSubmit}
                />
              </FormContainer>
            );
          }}
        </Formik>
      </ModalContainer>
    </InventoryItemFormContainer>
  );
}
